import {
    Box,
    VStack,
    Text,
    Accordion,
    AccordionItem,
    AccordionButton,
    AccordionPanel,
    AccordionIcon,
    Flex,
    IconButton,
    Badge,
    Tooltip,
} from '@chakra-ui/react';
import { memo, useCallback, useMemo } from 'react';
import { FiEye, FiEyeOff, FiLock, FiUnlock, FiPlus, FiCloudSnow } from 'react-icons/fi';
import { useAppStore, useLayers, useEntities, type Entity } from '../../state';
import { generateId } from '../../utils/helpers';
import { useTranslation } from 'react-i18next';

/**
 * Object list row (memoized): a selection click used to rebuild every row's
 * DOM because the inline Flex had a fresh onClick closure per render and no
 * memo boundary. Rows now only re-render when their entity data or their own
 * selected flag changes.
 */
const EntityRow = memo(function EntityRow({ entity, selected, onSelect }: {
    entity: Entity;
    selected: boolean;
    onSelect: (id: string, additive: boolean) => void;
}) {
    return (
        <Flex
            px={3}
            py={1.5}
            align="center"
            gap={2}
            cursor="pointer"
            bg={selected ? 'whiteAlpha.100' : 'transparent'}
            _hover={{ bg: 'whiteAlpha.50' }}
            onClick={(e) => onSelect(entity.id, e.ctrlKey || e.shiftKey)}
        >
            <Text flex="1" fontSize="sm" color="gray.200" noOfLines={1}>
                {entity.name}
            </Text>
            <Text fontSize="xs" color="gray.500">
                {entity.type}
            </Text>
        </Flex>
    );
});

/**
 * Left Sidebar Component — the body of the 2D/3D workbenches' "Project
 * Browser" side panel. Rendered inside the shared panel shell
 * (@suite/panels), which owns the panel chrome: dock edge, width, resize,
 * collapse, float and the tab header (registered as the 'sidebar' panel in
 * src/modules/cad/panels/registerCadPanels.tsx). This component renders
 * content only.
 *
 * Contains:
 * - Layer Manager (2D-style)
 * - Object List
 */
export function Sidebar() {
    const { t } = useTranslation();
    const layers = useLayers();
    const entities = useEntities();
    const selectedIds = useAppStore((s) => s.selectedIds);
    const select = useAppStore((s) => s.select);
    const addLayer = useAppStore((s) => s.addLayer);
    const activeLayerId = useAppStore((s) => s.activeLayerId);
    const setActiveLayer = useAppStore((s) => s.setActiveLayer);
    const updateLayer = useAppStore((s) => s.updateLayer);

    // Memoized list derivations: the sort ran in the render body before, so
    // every unrelated store write re-sorted and rebuilt the arrays.
    const layerList = useMemo(() => Object.values(layers).sort((a, b) => a.order - b.order), [layers]);
    const entityList = useMemo(() => Object.values(entities), [entities]);
    const selectedIdSet = useMemo(() => new Set(selectedIds), [selectedIds]);

    // Stable row handler: keeps EntityRow's memo boundary effective. Reads
    // selectedIds through getState() so the callback never goes stale and the
    // additive-append behavior matches the original inline handler exactly.
    const handleEntitySelect = useCallback((id: string, additive: boolean) => {
        const ids = useAppStore.getState().selectedIds;
        select(additive ? [...ids, id] : [id]);
    }, [select]);

    const handleAddLayer = () => {
        const order = layerList.length;
        const id = generateId();
        addLayer({
            id,
            name: t('ui.sidebar.newLayerName', { n: order + 1 }),
            color: '#808080',
            visible: true,
            locked: false,
            order,
        });
        setActiveLayer(id);
    };

    return (
        <Box data-tour-id="task-panels">
            {/* Accordion Sections */}
            <Accordion defaultIndex={[0, 1]} allowMultiple>
                    {/* Layers Section */}
                    <AccordionItem border="none">
                        <AccordionButton px={3} py={2} _hover={{ bg: 'whiteAlpha.50' }}>
                            <Flex flex="1" align="center" gap={2}>
                                <Text fontSize="xs" fontWeight="medium" color="gray.400">{t('ui.sidebar.layers')}</Text>
                                <Badge size="sm" colorScheme="blue">{layerList.length}</Badge>
                            </Flex>
                            <AccordionIcon />
                        </AccordionButton>
                        <AccordionPanel p={0}>
                            <VStack spacing={0} align="stretch">
                                {layerList.map((layer) => (
                                    <Flex
                                        key={layer.id}
                                        px={3}
                                        py={1.5}
                                        align="center"
                                        gap={2}
                                        cursor="pointer"
                                        bg={activeLayerId === layer.id ? 'whiteAlpha.100' : 'transparent'}
                                        _hover={{ bg: 'whiteAlpha.50' }}
                                        onClick={() => setActiveLayer(layer.id)}
                                    >
                                        {/* Layer Color Swatch */}
                                        <Box
                                            w={3}
                                            h={3}
                                            borderRadius="sm"
                                            bg={layer.color}
                                            border="1px solid"
                                            borderColor="whiteAlpha.300"
                                        />

                                        {/* Layer Name */}
                                        <Text flex="1" fontSize="sm" color="gray.200" noOfLines={1}>
                                            {layer.name}
                                        </Text>

                                        {/* Visibility Toggle */}
                                        <Tooltip label={layer.visible ? t('ui.sidebar.hide') : t('ui.sidebar.show')}>
                                            <IconButton
                                                icon={layer.visible ? <FiEye /> : <FiEyeOff />}
                                                size="xs"
                                                variant="ghost"
                                                aria-label={t('ui.sidebar.toggleVisibility')}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    updateLayer(layer.id, { visible: !layer.visible });
                                                }}
                                            />
                                        </Tooltip>

                                        {/* Freeze Toggle (LAYFRZ): off + out of regen/snap */}
                                        <Tooltip label={layer.frozen ? t('ui.sidebar.thaw') : t('ui.sidebar.freeze')}>
                                            <IconButton
                                                icon={<FiCloudSnow />}
                                                size="xs"
                                                variant="ghost"
                                                color={layer.frozen ? 'cyan.300' : undefined}
                                                aria-label={t('ui.sidebar.toggleFreeze')}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    updateLayer(layer.id, { frozen: !layer.frozen, visible: layer.frozen });
                                                }}
                                            />
                                        </Tooltip>

                                        {/* Lock Toggle */}
                                        <Tooltip label={layer.locked ? t('ui.sidebar.unlock') : t('ui.sidebar.lock')}>
                                            <IconButton
                                                icon={layer.locked ? <FiLock /> : <FiUnlock />}
                                                size="xs"
                                                variant="ghost"
                                                aria-label={t('ui.sidebar.toggleLock')}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    updateLayer(layer.id, { locked: !layer.locked });
                                                }}
                                            />
                                        </Tooltip>
                                    </Flex>
                                ))}

                                {/* Add Layer Button */}
                                <Flex
                                    px={3}
                                    py={1.5}
                                    align="center"
                                    gap={2}
                                    cursor="pointer"
                                    color="gray.500"
                                    _hover={{ bg: 'whiteAlpha.50', color: 'gray.300' }}
                                    onClick={handleAddLayer}
                                >
                                    <FiPlus />
                                    <Text fontSize="sm">{t('ui.sidebar.addLayer')}</Text>
                                </Flex>
                            </VStack>
                        </AccordionPanel>
                    </AccordionItem>

                    {/* Objects Section */}
                    <AccordionItem border="none">
                        <AccordionButton px={3} py={2} _hover={{ bg: 'whiteAlpha.50' }}>
                            <Flex flex="1" align="center" gap={2}>
                                <Text fontSize="xs" fontWeight="medium" color="gray.400">{t('ui.sidebar.objects')}</Text>
                                <Badge size="sm" colorScheme="green">{entityList.length}</Badge>
                            </Flex>
                            <AccordionIcon />
                        </AccordionButton>
                        <AccordionPanel p={0}>
                            <VStack spacing={0} align="stretch">
                                {entityList.length === 0 ? (
                                    <Box px={3} py={2}>
                                        <Text fontSize="sm" color="gray.500">{t('ui.sidebar.noObjectsInScene')}</Text>
                                    </Box>
                                ) : (
                                    entityList.map((entity) => (
                                        <EntityRow
                                            key={entity.id}
                                            entity={entity}
                                            selected={selectedIdSet.has(entity.id)}
                                            onSelect={handleEntitySelect}
                                        />
                                    ))
                                )}
                            </VStack>
                        </AccordionPanel>
                    </AccordionItem>
                </Accordion>
        </Box>
    );
}
